import axiosClient from "./apiClient";

/**
 * Giving somebody who belongs nowhere a home of their own.
 *
 * One call, `POST api/Auth0/v1/me/family` (REQ-TEN-3). It carries no role clause
 * server-side, deliberately: a caller with no tenant holds no tenant role, so any
 * role gate would refuse exactly the people it exists for.
 *
 * Safe to call twice. The tenant id is derived from the caller's Auth0 subject and
 * the row is written conditionally on its own absence, so a retry or a second device
 * converges on one household rather than creating two -- which is why this has no
 * client-side de-duplication of its own beyond the gate's one-attempt flag.
 */
export type ProvisionedTenant = {
  tenant_id: string;
  tenant_name?: string | null;
  /**
   * False when the household was already there. Not an error.
   *
   * Also not read by this client, for the same reason as the flag below: the gate acts
   * identically either way.
   */
  created: boolean;
  /**
   * Whether the viewer must authenticate again. Always true, and load-bearing.
   *
   * The tenant claim is Auth0-signed and the current token was minted before the
   * household existed, so it carries no tenant and `/me` will keep reporting none
   * until a new token is issued. Nothing the API returns can change a token already
   * in the browser.
   *
   * Carried on the body rather than inferred from the status, because this answers
   * 200 for a caller who was already provisioned and 201 for one who was not, and both
   * need the same sign-in.
   *
   * **Nothing in this client reads it today** -- the gate signs in again on any
   * success, which is the same decision for every value this field can take. Typed and
   * documented because the API contract has it and a client that starts branching
   * (say, to skip the redirect) needs it to be here; an earlier version of this comment
   * claimed the client read it, which was not true.
   */
  reauthentication_required: boolean;
};

/**
 * Where a purchase link's token waits between arriving and being spendable.
 *
 * It arrives on `/welcome?t=`, and cannot be used until the person has signed up and
 * come back authenticated -- a round trip through Auth0 that replaces this page. So it
 * has to survive that, and `sessionStorage` is the right lifetime: one browser session,
 * gone when they close the tab, never shared with another.
 *
 * Wrapped because storage throws in a private window. Losing it costs them nothing they
 * cannot recover: without the vouch they verify their email as anybody would, which is
 * the ordinary path rather than a failure.
 */
const PURCHASE_TOKEN_KEY = "cts.purchase-token";

export function rememberPurchaseToken(token: string): void {
  try {
    window.sessionStorage.setItem(PURCHASE_TOKEN_KEY, token);
  } catch {
    // See above: a lost token means ordinary email verification, not a dead end.
  }
}

/** Reads it and clears it, because it is spendable exactly once. */
export function takePurchaseToken(): string | null {
  try {
    const token = window.sessionStorage.getItem(PURCHASE_TOKEN_KEY);
    window.sessionStorage.removeItem(PURCHASE_TOKEN_KEY);
    return token;
  } catch {
    return null;
  }
}

/**
 * @param ownHomeRequested
 * The caller is asking for a home of their own despite belonging to an organization,
 * or having been invited to one (REQ-TEN-3).
 *
 * **Sent only when true, and never defaulted.** The server reads an absent field as
 * false. It used to refuse an organization's member outright, and what replaced that
 * refusal is deliberateness and nothing else -- the danger was never the state, it was
 * reaching it by accident, since somebody signing in without organization context is
 * indistinguishable from a consumer. A client that always sent the field would make a
 * deliberate request indistinguishable from the automatic path, which is the one
 * distinction the whole design rests on.
 */
export async function provisionMyFamily(
  { ownHomeRequested = false }: { ownHomeRequested?: boolean } = {}
): Promise<ProvisionedTenant> {
  // Taken, not read: the server spends it once anyway, so holding on to a token that
  // has already been presented would only mean sending a stale one on a later attempt.
  const purchaseToken = takePurchaseToken();

  const response = await axiosClient.post(
    "Auth0/v1/me/family",
    {
      ...(purchaseToken ? { purchase_token: purchaseToken } : {}),
      ...(ownHomeRequested ? { own_home_requested: true } : {}),
    }
  );

  // The API wraps success payloads, and the shape differs between its own
  // ApiResponse envelope and a bare body. Both are unwrapped here for the same
  // reason sendInvitation does it: a caller reading `.data.data` once and `.data`
  // elsewhere is how one of these reads silently became undefined before.
  return (response.data?.data ?? response.data) as ProvisionedTenant;
}
